import * as THREE from 'three';
import { LIGHT_PRISM, EARTH_POSITION } from './constants';

/**
 * Creates a circular texture for particles
 */
const createParticleTexture = () => {
  const canvas = document.createElement('canvas');
  canvas.width = 32;
  canvas.height = 32;
  const context = canvas.getContext('2d');
  
  // Create a radial gradient for a soft-edged circular particle
  const gradient = context.createRadialGradient(
    16, 16, 0,    // inner circle center and radius
    16, 16, 16    // outer circle center and radius
  );
  
  gradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
  gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
  
  context.fillStyle = gradient;
  context.fillRect(0, 0, 32, 32);
  
  const texture = new THREE.CanvasTexture(canvas);
  return texture;
};

/**
 * Creates and returns a light prism object above the Earth
 */
const createLightPrism = () => {
  // Create a light prism group to hold all prisms and beams
  const mainGroup = new THREE.Group();
  
  // Function to create a triangular prism with the specified radius and height
  const createPrismGeometry = (radius, height) => {
    const geometry = new THREE.BufferGeometry();
    
    // Calculate height for an equilateral triangle (using side length = 2 * radius)
    const sideLength = 2 * radius;
    const triangleHeight = sideLength * Math.sqrt(3) / 2;
    
    // Define the vertices for a triangular prism (with equilateral triangular cross-section)
    // The flat side will be on top and the camera will see the triangular profile
    const vertices = new Float32Array([
      // Front face (equilateral triangle)
      -radius, 0, -height/2,  // bottom left
      radius, 0, -height/2,   // bottom right
      0, -triangleHeight, -height/2,      // bottom vertex
      
      // Back face (equilateral triangle)
      -radius, 0, height/2,   // bottom left
      radius, 0, height/2,    // bottom right
      0, -triangleHeight, height/2,       // bottom vertex
      
      // Top face (rectangle - flat side)
      -radius, 0, -height/2,  // front left
      radius, 0, -height/2,   // front right
      radius, 0, height/2,    // back right
      -radius, 0, height/2,   // back left
      
      // Left face (rectangle)
      -radius, 0, -height/2,  // top front
      -radius, 0, height/2,   // top back
      0, -triangleHeight, height/2,       // bottom back
      0, -triangleHeight, -height/2,      // bottom front
      
      // Right face (rectangle)
      radius, 0, -height/2,   // top front
      radius, 0, height/2,    // top back
      0, -triangleHeight, height/2,       // bottom back
      0, -triangleHeight, -height/2       // bottom front
    ]);
    
    // Define the face indices
    const indices = [
      // Front face
      0, 1, 2,
      
      // Back face
      5, 4, 3,
      
      // Bottom face
      6, 7, 8,
      8, 9, 6,
      
      // Left face
      10, 11, 12,
      12, 13, 10,
      
      // Right face
      17, 16, 15,
      15, 14, 17
    ];
    
    // Set up the buffers
    geometry.setIndex(indices);
    geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
    
    // Calculate normals automatically
    geometry.computeVertexNormals();
    
    return geometry;
  };
  
  // Function to create crystal material for prisms
  const createPrismMaterial = (color = LIGHT_PRISM.PRISM_COLOR) => {
    return new THREE.MeshPhysicalMaterial({
      color: color,
      metalness: 0.1,           // Low metalness for glass look
      roughness: 0.0,           // No roughness for perfect clarity
      transmission: 0.95,       // High transmission for transparency
      transparent: true,
      opacity: 0.7,
      side: THREE.DoubleSide,
      reflectivity: 0.5,        // Some reflectivity for crystal surface
      ior: 1.5,                 // Index of refraction like crystal/glass
      envMapIntensity: 1.0,     // Environment reflections
      clearcoat: 1.0,           // Glass-like coating
      clearcoatRoughness: 0.0,  // Perfect clearcoat for shine
      emissive: 0xCCFFFF,       // Slight blue-white glow
      emissiveIntensity: 0.3,   // Brightness against dark background
      attenuationColor: 0xEEFFFF, // Slight blue tint for thickness
      attenuationDistance: 2.0,  // How far light travels in material
    });
  };
  
  // Create environment map for mirror-like reflections
  const cubeRenderTarget = new THREE.WebGLCubeRenderTarget(256);
  const cubeCamera = new THREE.CubeCamera(0.1, 1000, cubeRenderTarget);
  
  // ===== FIRST PRISM =====
  // Create the first prism group
  const prismGroup = new THREE.Group();
  
  // Create first prism geometry
  const prismGeometry = createPrismGeometry(LIGHT_PRISM.RADIUS, LIGHT_PRISM.HEIGHT);
  const prismMaterial = createPrismMaterial();
  prismMaterial.envMap = cubeRenderTarget.texture;
  
  // Create the first prism mesh
  const prism = new THREE.Mesh(prismGeometry, prismMaterial);
  prismGroup.add(prism);
  
  // Position the first prism
  prismGroup.position.set(
    LIGHT_PRISM.POSITION.X,
    LIGHT_PRISM.POSITION.Y,
    LIGHT_PRISM.POSITION.Z
  );
  
  // Add first prism to main group
  mainGroup.add(prismGroup);
  
  // ===== SECOND PRISM =====
  // Create the second prism group
  const secondPrismGroup = new THREE.Group();
  
  // Create second prism geometry (reuse the same geometry)
  const secondPrismMaterial = createPrismMaterial();
  secondPrismMaterial.envMap = cubeRenderTarget.texture;
  
  // Create the second prism mesh
  const secondPrism = new THREE.Mesh(prismGeometry, secondPrismMaterial);
  secondPrismGroup.add(secondPrism);
  
  // Position the second prism
  secondPrismGroup.position.set(
    LIGHT_PRISM.SECOND_PRISM.POSITION.X,
    LIGHT_PRISM.SECOND_PRISM.POSITION.Y,
    LIGHT_PRISM.SECOND_PRISM.POSITION.Z
  );
  
  // Add second prism to main group
  mainGroup.add(secondPrismGroup);
  
  // ===== BEAM PARTICLES =====
  
  // 1. Airplane to first prism beam (original beam)
  const particleCount = LIGHT_PRISM.BEAM.PARTICLE_COUNT;
  const particleGeometry = new THREE.BufferGeometry();
  const particlePositions = new Float32Array(particleCount * 3); // x, y, z for each particle
  const particleVelocities = new Float32Array(particleCount); // Store progress along path
  
  // Initialize particles with random positions along the path
  for (let i = 0; i < particleCount; i++) {
    particleVelocities[i] = Math.random(); // Random starting progress (0-1)
    // Positions will be set in the update function
    particlePositions[i * 3] = 0;
    particlePositions[i * 3 + 1] = 0;
    particlePositions[i * 3 + 2] = 0;
  }
  
  particleGeometry.setAttribute('position', new THREE.BufferAttribute(particlePositions, 3));
  
  // Create particle material with custom texture
  const particleMaterial = new THREE.PointsMaterial({
    color: 0xffffff,
    size: LIGHT_PRISM.BEAM.PARTICLE_SIZE,
    transparent: true,
    opacity: 0.8,
    map: createParticleTexture(),
    blending: THREE.AdditiveBlending,
    depthWrite: false
  });
  
  const particles = new THREE.Points(particleGeometry, particleMaterial);
  
  // 2. First prism to second prism connecting beam (technicolor)
  const connectingBeamCount = LIGHT_PRISM.CONNECTING_BEAM.PARTICLE_COUNT;
  const connectingBeamGeometry = new THREE.BufferGeometry();
  const connectingBeamPositions = new Float32Array(connectingBeamCount * 3);
  const connectingBeamVelocities = new Float32Array(connectingBeamCount);
  
  // Initialize connecting beam particles
  for (let i = 0; i < connectingBeamCount; i++) {
    connectingBeamVelocities[i] = Math.random();
    connectingBeamPositions[i * 3] = 0;
    connectingBeamPositions[i * 3 + 1] = 0;
    connectingBeamPositions[i * 3 + 2] = 0;
  }
  
  connectingBeamGeometry.setAttribute('position', new THREE.BufferAttribute(connectingBeamPositions, 3));
  
  // Create technicolor connecting beam material
  const connectingBeamMaterial = new THREE.PointsMaterial({
    color: LIGHT_PRISM.CONNECTING_BEAM.COLOR,
    size: LIGHT_PRISM.CONNECTING_BEAM.PARTICLE_SIZE,
    transparent: true,
    opacity: 0.8,
    map: createParticleTexture(),
    blending: THREE.AdditiveBlending,
    depthWrite: false
  });
  
  const connectingBeamParticles = new THREE.Points(connectingBeamGeometry, connectingBeamMaterial);
  
  // Removed terrain particle tracking
  
  return {
    mainGroup,
    prismGroup,
    prism,
    secondPrismGroup,
    secondPrism,
    // Original beam (airplane to first prism)
    particles,
    particleGeometry,
    particlePositions,
    particleVelocities,
    // Connecting beam (first prism to second prism)
    connectingBeamParticles,
    connectingBeamGeometry,
    connectingBeamPositions,
    connectingBeamVelocities,
    // Environment map
    cubeCamera,
    cubeRenderTarget,
    // Geometries and materials
    geometries: {
      prismGeometry,
      particleGeometry,
      connectingBeamGeometry
    },
    materials: {
      prismMaterial,
      secondPrismMaterial,
      particleMaterial,
      connectingBeamMaterial
    }
  };
};

/**
 * Updates all particle flows and beams in the scene
 */
const updateLightBeam = (airplane, lightPrism, terrain, scene, renderer) => {
  const { 
    prism,
    secondPrism,
    particles, 
    particleGeometry, 
    particlePositions, 
    particleVelocities,
    // Connecting beam
    connectingBeamParticles,
    connectingBeamGeometry,
    connectingBeamPositions,
    connectingBeamVelocities,
    // Environment map
    cubeCamera,
    cubeRenderTarget
  } = lightPrism;
  const { airplaneGroup } = airplane;
  
  // Get positions of all key elements
  const airplanePosition = new THREE.Vector3();
  airplaneGroup.getWorldPosition(airplanePosition);
  
  const firstPrismPosition = new THREE.Vector3();
  lightPrism.prismGroup.getWorldPosition(firstPrismPosition);
  
  const secondPrismPosition = new THREE.Vector3();
  lightPrism.secondPrismGroup.getWorldPosition(secondPrismPosition);
  
  // === 1. UPDATE ORIGINAL BEAM (AIRPLANE TO FIRST PRISM) ===
  
  // Calculate the direction and length of the path
  const flowDirection = new THREE.Vector3().subVectors(firstPrismPosition, airplanePosition).normalize();
  const pathLength = airplanePosition.distanceTo(firstPrismPosition);
  
  // Update particle positions
  const particleCount = particleVelocities.length;
  const time = Date.now() * 0.001;
  const speed = LIGHT_PRISM.BEAM.PARTICLE_SPEED;
  const flowWidth = LIGHT_PRISM.BEAM.FLOW_WIDTH;
  
  for (let i = 0; i < particleCount; i++) {
    // Update particle progress (velocity determines speed)
    particleVelocities[i] += speed * 0.01 * (0.5 + Math.random() * 0.5);
    
    // Reset particles that reach the end
    if (particleVelocities[i] > 1.0) {
      particleVelocities[i] = 0.0;
    }
    
    // Get particle position along the path (airplane to prism)
    const t = particleVelocities[i];
    
    // Calculate position on the line with slight random deviation
    const particlePos = new THREE.Vector3()
      .copy(airplanePosition)
      .addScaledVector(flowDirection, t * pathLength);
    
    // Add significant deviation for a wider, more visible flow
    // Use sin wave for a spiral effect along the path
    const angle = t * 10 + i * 0.1;
    
    // Apply the flowWidth parameter directly - wider at start, narrower at end
    const deviation = flowWidth * (1 - t * 0.7); 
    
    // Apply a stronger deviation to make the width more noticeable
    particlePos.x += Math.sin(angle) * deviation;
    particlePos.y += Math.cos(angle) * deviation;
    particlePos.z += Math.sin(angle * 0.7) * deviation;
    
    // Update the position in the array
    particlePositions[i * 3] = particlePos.x;
    particlePositions[i * 3 + 1] = particlePos.y;
    particlePositions[i * 3 + 2] = particlePos.z;
  }
  
  // Tell Three.js to update the particles
  particleGeometry.attributes.position.needsUpdate = true;
  
  
  // === 2. UPDATE CONNECTING BEAM (FIRST PRISM TO SECOND PRISM) ===
  
  // Get midpoints of both prisms for beam origin/target
  const firstPrismMidY = firstPrismPosition.y - 0.5; // Move down to the middle of the first prism
  const secondPrismMidY = secondPrismPosition.y - 0.5; // Move down to the middle of the second prism
  
  const firstPrismMidpoint = new THREE.Vector3(firstPrismPosition.x, firstPrismMidY, firstPrismPosition.z);
  const secondPrismMidpoint = new THREE.Vector3(secondPrismPosition.x, secondPrismMidY, secondPrismPosition.z);
  
  // Calculate direction and path length for connecting beam
  const connectingDirection = new THREE.Vector3().subVectors(secondPrismMidpoint, firstPrismMidpoint).normalize();
  const connectingPathLength = firstPrismMidpoint.distanceTo(secondPrismMidpoint);
  
  // Update connecting beam particles
  const connectingBeamCount = connectingBeamVelocities.length;
  const connectingSpeed = LIGHT_PRISM.CONNECTING_BEAM.PARTICLE_SPEED;
  const connectingFlowWidth = LIGHT_PRISM.CONNECTING_BEAM.FLOW_WIDTH;
  
  for (let i = 0; i < connectingBeamCount; i++) {
    // Update particle progress
    connectingBeamVelocities[i] += connectingSpeed * 0.01 * (0.5 + Math.random() * 0.5);
    
    // Reset particles that reach the end
    if (connectingBeamVelocities[i] > 1.0) {
      connectingBeamVelocities[i] = 0.0;
    }
    
    const t = connectingBeamVelocities[i];
    
    // Calculate position with rainbow/technicolor effect, starting from midpoint of first prism
    const particlePos = new THREE.Vector3()
      .copy(firstPrismMidpoint)
      .addScaledVector(connectingDirection, t * connectingPathLength);
    
    // Create larger, more visible rainbow pattern
    const angle = t * 12 + i * 0.1;
    const rainbowPhase = (t + time * 0.2) % 1.0; // Creates a flowing color effect
    
    // Technicolor beam needs larger deviation
    const deviation = connectingFlowWidth * (1 - t * 0.5);
    
    // Apply spiraling rainbow pattern
    particlePos.x += Math.sin(angle) * deviation;
    particlePos.y += Math.cos(angle) * deviation;
    particlePos.z += Math.sin(angle * 0.7) * deviation;
    
    // Update the position in the array
    connectingBeamPositions[i * 3] = particlePos.x;
    connectingBeamPositions[i * 3 + 1] = particlePos.y;
    connectingBeamPositions[i * 3 + 2] = particlePos.z;
  }
  
  // Update connecting beam particles
  connectingBeamGeometry.attributes.position.needsUpdate = true;
  
  // === 3. UPDATE ENVIRONMENT REFLECTIONS ===
  if (scene && renderer && cubeCamera) {
    // Temporarily hide the prisms so they don't appear in their own reflection
    prism.visible = false;
    secondPrism.visible = false;
    
    // Update the cube camera's position to match the active prism
    const prismWorldPos = new THREE.Vector3();
    prism.getWorldPosition(prismWorldPos);
    cubeCamera.position.copy(prismWorldPos);
    
    // Update the cubemap
    cubeCamera.update(renderer, scene);
    
    // Make the prisms visible again
    prism.visible = true;
    secondPrism.visible = true;
  }
};

/**
 * Cleanup function for light prism objects
 */
const cleanupLightPrism = (scene, lightPrism) => {
  const { 
    mainGroup, 
    particles, 
    connectingBeamParticles,
    geometries, 
    materials, 
    cubeRenderTarget 
  } = lightPrism;
  
  // Remove all groups and particle systems
  scene.remove(mainGroup);
  scene.remove(particles);
  scene.remove(connectingBeamParticles);
  
  // Dispose of all geometries
  if (geometries) {
    Object.values(geometries).forEach(geometry => {
      if (geometry) {
        if (Array.isArray(geometry)) {
          geometry.forEach(g => {
            if (g) g.dispose();
          });
        } else {
          geometry.dispose();
        }
      }
    });
  }
  
  // Dispose of all materials
  if (materials) {
    Object.values(materials).forEach(material => {
      if (material) {
        if (Array.isArray(material)) {
          material.forEach(m => {
            if (m) {
              if (m.map) m.map.dispose();
              m.dispose();
            }
          });
        } else {
          if (material.map) material.map.dispose();
          material.dispose();
        }
      }
    });
  }
  
  // Dispose of cube render target
  if (cubeRenderTarget) {
    cubeRenderTarget.dispose();
  }
};

export { createLightPrism, updateLightBeam, cleanupLightPrism };